/**
 * Authoritative DDD (Division of Developmental Disabilities) Gating Logic
 *
 * Rules:
 * - Selecting Arizona Medicaid (e.g. Mercy Care, Banner, Complete Health, UHCCP)
 *   must NOT automatically classify a client as DDD.
 * - DDD packet inclusion must strictly originate from an authoritative intake/program field:
 *   1. DDD / ALTCS Payer (e.g. 'DDD', 'AZ DES/DDD', 'DES/DDD', 'ALTCS', 'LTC')
 *   2. DES/DDD Support Coordinator assigned
 *   3. Arizona DDD District Code assigned (e.g. 'DW', 'DC', 'DE')
 *   4. WellSky / Focus Case Number assigned
 *   5. Existing DDD packet document record
 *   6. Explicit ddd_enrollment boolean flag
 */

export interface DddGatingCandidate {
  is_ddd?: boolean | null;
  ddd_enrollment?: boolean | null;
  primary_payer_name?: string | null;
  secondary_payer_name?: string | null;
  support_coordinator_name?: string | null;
  district_code?: string | null;
  wellsky_caseno?: string | null;
  ddd_packet_doc_id?: string | null;
}

export function isDddClient(clientOrForm: DddGatingCandidate | null | undefined): boolean {
  if (!clientOrForm) return false;

  // 1. Explicit boolean flag if present
  if (clientOrForm.is_ddd === true || clientOrForm.ddd_enrollment === true) return true;
  if (clientOrForm.is_ddd === false || clientOrForm.ddd_enrollment === false) return false;

  // 2. Strict DDD / ALTCS Payer check (NOT general Medicaid like Mercy Care, Banner, or Complete Health)
  const isDddPayer = (p?: string | null) => {
    if (!p) return false;
    const clean = p.trim().toUpperCase();
    const compact = clean.replace(/\s+/g, '');
    return (
      clean === 'DDD' ||
      clean === 'AZ DES/DDD' ||
      clean === 'DES/DDD' ||
      clean === 'DES-DDD' ||
      clean === 'DES / DDD' ||
      clean === 'ARIZONA DES / DDD' ||
      compact.includes('DES/DDD') ||
      compact.includes('DES-DDD') ||
      clean.includes('DIVISION OF DEVELOPMENTAL DISABILITIES') ||
      clean.includes('ALTCS') ||
      clean === 'LTC'
    );
  };

  if (isDddPayer(clientOrForm.primary_payer_name) || isDddPayer(clientOrForm.secondary_payer_name)) {
    return true;
  }

  // 3. DES/DDD Support Coordinator email ending in or containing azdes.gov
  if ((clientOrForm as any).support_coordinator_email && (clientOrForm as any).support_coordinator_email.toLowerCase().includes('azdes.gov')) {
    return true;
  }

  // 4. Authoritative DDD Case Data: Arizona DDD District Code assigned
  if (clientOrForm.district_code && clientOrForm.district_code.trim().length > 0) {
    return true;
  }

  // 5. Authoritative DDD Case Data: WellSky / Focus DDD Case Number
  if (clientOrForm.wellsky_caseno && clientOrForm.wellsky_caseno.trim().length > 0) {
    return true;
  }

  // 6. Pre-existing DDD packet document ID
  if (clientOrForm.ddd_packet_doc_id && clientOrForm.ddd_packet_doc_id.trim().length > 0) {
    return true;
  }

  return false;
}
